import { Box, Button, CircularProgress, Container, Typography } from '@mui/material'
import { useAtomValue } from 'jotai'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import useBar from '../components/common/Bar'
import GroupSessionForm from '../components/forms/GroupSessionForm'
import { currentUserAtom } from '../domains/user/AtomUsers'
import GroupSessionService from '../services/GroupSessionService'

export default function GroupSessionEditPage() {
  const navigate = useNavigate()
  const bar = useBar()

  const { id } = useParams()
  const user = useAtomValue(currentUserAtom)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [editFormData, setEditFormData] = useState(null)
  const [formErrors, setFormErrors] = useState({})

  // Load session data
  useEffect(() => {
    const fetchSession = async () => {
      try {
        setLoading(true)
        const data = await GroupSessionService.getGroupSession(id)

        if (!user?.roles?.includes('ADMIN') && !(data.instructorId === user?.resourceId || data.ownerId === user?.resourceId)) {
          bar.error('You do not have permission to edit this session')
          navigate('/events/sessions')
          return
        }

        setSession(data)
        setEditFormData(data)
      } catch (err) {
        bar.error(err.message || 'Failed to load session')
        navigate('/events/sessions')
      } finally {
        setLoading(false)
      }
    }

    fetchSession()
  }, [id, navigate, user])

  const onFormChange = useCallback((data) => {
    setEditFormData((prev) => {
      return { ...prev, ...data }
    })
  }, [])

  // Form validation
  const validateForm = (data) => {
    const errors = {}
    if (!data.title) errors.title = 'Please enter a session title'
    if (!data.sport) errors.sport = 'Please select a sport type'
    if (!data.skiResort) errors.skiResort = 'Please select a location'
    if (!data.price || data.price < 0) errors.price = 'Please enter a valid price'
    if (!data.minParticipants || data.minParticipants < 1) errors.minParticipants = 'Minimum participants must be greater than 0'
    if (!data.maxParticipants || data.maxParticipants < data.minParticipants) {
      errors.maxParticipants = 'Maximum participants must be greater than minimum'
    }
    if (!data.date) errors.date = 'Please select a session date'
    if (!data.times || data.times.length === 0) errors.times = 'Please add at least one time slot'

    // Validate that each time slot has both start and end times
    if (data.times) {
      data.times.forEach((timeSlot, index) => {
        if (!timeSlot.startTime || !timeSlot.endTime) {
          errors[`timeSlot${index}`] = 'Please complete the time slot'
        }
      })
    }

    return errors
  }

  // Handle form submission
  const handleSubmit = async () => {
    const errors = validateForm(editFormData)
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      bar.error('Please fix the form errors before submitting')
      return
    }

    try {
      setSubmitting(true)

      await GroupSessionService.updateGroupSession(id, editFormData)
      bar.success('Session updated successfully')
      navigate(`/events/sessions/${id}`)
    } catch (err) {
      bar.error(err.message || 'Failed to update session')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!session) {
    return null
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Edit Group Session
      </Typography>

      <GroupSessionForm formData={editFormData} formErrors={formErrors} onChange={onFormChange} />

      <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => navigate(-1)} disabled={submitting}>
          Cancel
        </Button>
        <Button loading={submitting} loadingPosition="start" color="info" variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ minWidth: 120 }}>
          Save Changes
        </Button>
      </Box>
    </Container>
  )
}
